import { FEATURES, IOrganizationUser } from "@home-health-notify/domain";
import WidgetsIcon from "@mui/icons-material/Widgets";
import CallIcon from "@mui/icons-material/Call";
import CalendarIcon from "@mui/icons-material/Event";
import FeedbackIcon from "@mui/icons-material/Feedback";
import AdminPanelIcon from "@mui/icons-material/ManageAccounts";
import MapIcon from "@mui/icons-material/Map";
import { IconButton, Tooltip } from "@mui/material";
import * as Sentry from "@sentry/react";
import {
  DomainContext,
  formatDisplayPhoneNumber,
  OrganizationContext,
  useTranslation,
} from "hhn-commons";
import { useContext } from "react";
import { Link } from "react-router";
import WhiteLogo from "../assets/images/HHNotify_square.svg";
import { RightDrawerContext } from "../context/RightDrawerContext";
import { OrganizationMenu } from "../header/organizationMenu/OrganizationMenu";
import { UserMenu } from "../header/userMenu/UserMenu";
import { MODAL_ROUTES } from "../routes";
import config from "../utils/Config";

import {
  BottomBox,
  CompanyLogoAvatar,
  RailAction,
  RailLabel,
  SidebarListWrapper,
  SidebarWrapper,
} from "./Sidebar.style";
import { ConnectionStatusButton } from "./ConnectionStatusButton";
import SidebarListItem from "./SidebarListItem";

const Sidebar = ({ onLogout }: { onLogout: () => void }) => {
  const { t } = useTranslation();
  const { domain } = useContext(DomainContext);
  const user = domain.user;
  const orgUsers = user?.getOrganizations() || [];
  const { closeDrawer } = useContext(RightDrawerContext);
  const { selectedOrgUser } = useContext(OrganizationContext);
  const canAccessAdminPanel = selectedOrgUser?.hasAccess(FEATURES.ADMIN_PANEL);
  const canAccessAdvanceSearch = selectedOrgUser?.hasAccess(
    FEATURES.ADVANCE_SEARCH,
  );
  const canExportChat = selectedOrgUser?.hasAccess(FEATURES.EXPORT_CHAT);
  const canAccessLabRequisitions =
    selectedOrgUser?.isLabIntegrationEnabled() &&
    selectedOrgUser?.hasAccess(FEATURES.ACCESS_LAB_REQUISITIONS);
  const canAccessEmr =
    !!selectedOrgUser?.isEmrSyncEnabled() &&
    (selectedOrgUser?.hasAccess(FEATURES.MANAGE_SYNC_QUEUE) ||
      selectedOrgUser?.hasAccess(FEATURES.MANAGE_SYNC_SETTINGS));
  const canCreate =
    !!selectedOrgUser?.hasAccess(FEATURES.CREATE_INTERNAL_GROUP) ||
    !!selectedOrgUser?.hasAccess(FEATURES.CREATE_PATIENT_GROUP) ||
    !!selectedOrgUser?.hasAccess(FEATURES.CREATE_BROADCAST) ||
    !!selectedOrgUser?.hasAccess(FEATURES.VIEW_ORGANIZATION_MEDIA);
  const canAccessMore =
    canAccessEmr ||
    canAccessLabRequisitions ||
    canAccessAdvanceSearch ||
    canExportChat ||
    canAccessAdminPanel;
  const organizationNo = selectedOrgUser?.getOrganization()?.organizationNo;
  const organizationNoDigits = organizationNo?.replace(/\D/g, "") ?? "";
  const formattedOrganizationNo = organizationNo
    ? formatDisplayPhoneNumber(
        organizationNoDigits.length > 10
          ? `+${organizationNoDigits.slice(0, organizationNoDigits.length - 10)}`
          : "+1",
        organizationNoDigits.slice(-10),
      )
    : undefined;
  const openSentryFeedbackForm = async () => {
    if (Sentry.isInitialized()) {
      const feedback = Sentry.getFeedback();
      const form = await feedback?.createForm();
      if (form) {
        form.appendToDom();
        form.open();
      }
    }
  };
  return (
    <SidebarWrapper>
      <CompanyLogoAvatar
        alt={t("companyName")}
        src={WhiteLogo}
        variant="square"
      />
      <SidebarListWrapper>
        {orgUsers.map((orgUser: IOrganizationUser) => {
          return (
            <SidebarListItem
              key={orgUser.getOrgUniqueIdentifer()}
              orgUser={orgUser}
            />
          );
        })}
      </SidebarListWrapper>

      <BottomBox>
        <ConnectionStatusButton />
        {Sentry.isInitialized() && (
          <Tooltip title="Share feedback or bug" placement="right-end">
            <RailAction>
              <IconButton
                size="large"
                color="inherit"
                onClick={openSentryFeedbackForm}
              >
                <FeedbackIcon htmlColor="white" />
              </IconButton>
              <RailLabel>Feedback</RailLabel>
            </RailAction>
          </Tooltip>
        )}
        {canAccessAdminPanel && (
          <Tooltip title="Open admin panel" placement="right-end">
            <RailAction>
              <IconButton
                size="large"
                color="inherit"
                component={Link}
                target="_blank"
                to={config.adminPanelUrl}
              >
                <AdminPanelIcon htmlColor="white" />
              </IconButton>
              <RailLabel>Admin</RailLabel>
            </RailAction>
          </Tooltip>
        )}
        {organizationNo && (
          <Tooltip
            title={`Call agency: ${formattedOrganizationNo}`}
            placement="right-end"
          >
            <RailAction>
              <IconButton
                size="large"
                color="inherit"
                component="a"
                href={`tel:${organizationNo}`}
                data-testid="call-agency"
              >
                <CallIcon htmlColor="white" />
              </IconButton>
              <RailLabel>Call</RailLabel>
            </RailAction>
          </Tooltip>
        )}
        <Tooltip title="Open calendar" placement="right-end">
          <RailAction>
            <IconButton
              onClick={() => {
                closeDrawer();
              }}
              size="large"
              color="inherit"
              component={Link}
              to={`?modal=${MODAL_ROUTES.CALENDAR}`}
            >
              <CalendarIcon htmlColor="white" />
            </IconButton>
            <RailLabel>Calendar</RailLabel>
          </RailAction>
        </Tooltip>
        {canAccessAdvanceSearch && (
          <Tooltip title="Open care map" placement="right-end">
            <RailAction>
              <IconButton
                onClick={() => {
                  closeDrawer();
                }}
                size="large"
                color="inherit"
                component={Link}
                to={`?modal=${MODAL_ROUTES.CARE_MAP}`}
                data-testid="open-care-map"
              >
                <MapIcon htmlColor="white" />
              </IconButton>
              <RailLabel>Care Map</RailLabel>
            </RailAction>
          </Tooltip>
        )}
        {canCreate && (
          <RailAction>
            <OrganizationMenu />
            <RailLabel>Create</RailLabel>
          </RailAction>
        )}
        {canAccessMore && (
          <Tooltip title="Open more" placement="right-end">
            <RailAction>
              <IconButton
                onClick={() => {
                  closeDrawer();
                }}
                size="large"
                color="inherit"
                component={Link}
                to={`?modal=${MODAL_ROUTES.MORE}`}
                data-testid="open-more"
              >
                <WidgetsIcon htmlColor="white" />
              </IconButton>
              <RailLabel>More</RailLabel>
            </RailAction>
          </Tooltip>
        )}
        <RailAction>
          <UserMenu onLogout={onLogout} />
          <RailLabel>You</RailLabel>
        </RailAction>
      </BottomBox>
    </SidebarWrapper>
  );
};
export default Sidebar;
